import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  makeStyles,
  Typography,
  Card,
  Box,
  Container,
  Link,
  Button
} from '@material-ui/core';

import Page from 'src/shared/ui/components/Page';
import Form from 'src/shared/ui/components/Form';
import processFormErrorMessages from 'src/shared/ui/components/Form/FormErrorMessage';

import validateSchema from 'src/shared/models/SchemaValidator';
import ProductType from 'src/product-type/models/ProductType';

import createProductType from 'src/product-type/use-cases/createProductType';
import updateProductTypeById from 'src/product-type/use-cases/updateProductTypeById';
import getProductTypeList from 'src/product-type/use-cases/getProductTypeList';
import getProductTypeById from 'src/product-type/use-cases/getProductTypeById';

import getProductCategoryList from 'src/product-category/use-cases/getProductCategoryList';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  saveButton: {
    top: '-20px'
  },
  backButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  scrollBox: {
    overflow: 'scroll',
    height: '80vh'
  }
}));

const ProductTypeFormView = () => {
  const classes = useStyles();
  const [formFields, setFormFields] = useState([]);
  const [isRequesting, setIsRequesting] = useState(false);
  const { productTypeId } = useParams();
  const navigate = useNavigate();

  const initFormFields = async (fields) => {
    const productCategoryList = await getProductCategoryList({
      page: null,
      limit: null,
      orderBy: ''
    });
    const productType = ProductType({
      fields,
      productCategoryList: productCategoryList
    });

    setFormFields(productType);
  };

  const fetchForm = async () => {
    const productType = await getProductTypeById(productTypeId);

    initFormFields(productType);
  };

  useEffect(() => {
    if (productTypeId === 'new') {
      initFormFields();
    } else {
      fetchForm();
    }
  }, []);

  const validateFormFields = async (formFieldsValues) => {
    const validatedFormFields = await validateSchema(formFieldsValues);
    formFieldsValues = processFormErrorMessages(
      validatedFormFields,
      formFieldsValues
    );
    setFormFields(formFieldsValues.fields);

    return {
      errors: formFieldsValues.errors,
      fields: validatedFormFields
    };
  };

  const handleChangeFormFieldsValues = (formFieldsValues, target) => {
    if (target.name === 'productCategoryId') {
      let fields = {
        [target.name]: target.value,
        productSubcategoryList: null
      };

      initFormFields(fields);

      return;
    }

    setFormFields(formFieldsValues);
  };

  const postForm = async () => {
    if (isRequesting) {
      return;
    }

    const validatedForm = await validateFormFields(formFields);
    if (validatedForm.errors) {
      alert('Rellene todos los campos correctamente.');
      return;
    }

    setIsRequesting(true);

    let result;
    if (productTypeId === 'new') {
      result = await createProductType(validatedForm.fields);
    } else {
      result = await updateProductTypeById(validatedForm.fields, productTypeId);
    }

    setIsRequesting(false);

    if (typeof result.id === 'undefined') {
      alert('Se ha producido un error, vuelva a intentarlo');
    } else {
      navigate('/app/product-types');
    }
  };

  return (
    <Page className={classes.root} title="Tipos de productos">
      <Container maxWidth={false}>
        <Box className={classes.navigation} display="flex">
          <Typography color="textSecondary" variant="body2">
            <Link component={RouterLink} to="/app/product-types">
              Tipos de productos
            </Link>
            {` >  ${
              formFields.length && formFields[3].value.length
                ? formFields[3].value
                : 'Nuevo'
            }`}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Link component={RouterLink} to="/app/product-types">
            <Button className={classes.backButton}>{'Volver a listado'}</Button>
          </Link>
          <Button
            color="primary"
            variant="contained"
            className={classes.saveButton}
            onClick={postForm}
          >
            Guardar
          </Button>
        </Box>
        <Card>
          <Form
            fields={formFields}
            onChangeFields={handleChangeFormFieldsValues}
          />
        </Card>
      </Container>
    </Page>
  );
};

export default ProductTypeFormView;
